import { watch } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import { useUserAuthStore } from 'src/stores/user/auth'
import { readLocaleSettings } from 'src/core/locale/context'
import { localeToApiLanguage, normalizeLocale } from 'src/core/locale/normalize'
import { getAuthoritativeStoredLocale } from 'src/core/locale/runtime'
import updateLanguage from 'src/apollo/mutations/updateLanguage.gql'

export function initProfileLocaleSync({ app, authStore, mutate, settings = readLocaleSettings() }) {
  if (settings.platform !== 'olimpbet') {
    return
  }

  let identity = null
  let revision = 0
  let ready = Promise.resolve()
  const attempted = new Set()
  const stop = watch(
    [() => authStore.getAuth, () => authStore.getUser?.id, () => authStore.getUser?.language],
    (state) => {
      ready = syncProfileLocale(state)
    },
    { immediate: true },
  )

  async function syncProfileLocale([isAuth, userId, profileLanguage]) {
    const requestRevision = ++revision
    const nextIdentity = isAuth && userId != null ? userId : null
    if (identity !== nextIdentity) {
      identity = nextIdentity
      attempted.clear()
    }
    if (identity === null) {
      return
    }

    const locale = getAuthoritativeStoredLocale()
    if (!locale || normalizeLocale(profileLanguage, settings.availableLocales) === locale || attempted.has(locale)) {
      return
    }
    attempted.add(locale)

    try {
      const { data } = await mutate({
        mutation: updateLanguage,
        variables: { language: localeToApiLanguage(locale), auto: true },
      })
      if (
        requestRevision !== revision ||
        !authStore.getAuth ||
        authStore.getUser?.id !== userId ||
        getAuthoritativeStoredLocale() !== locale
      ) {
        return
      }
      const language = normalizeLocale(data?.userEdit?.user?.language, settings.availableLocales)
      if (language) {
        authStore.updateUserField({ key: 'language', value: language })
      }
    } catch (error) {
      console.error('[Locale] Failed to sync profile language:', error)
    }
  }

  app.onUnmount(() => {
    revision++
    stop()
  })
  return { stop, ready }
}

export default function ({ app }) {
  const settings = readLocaleSettings()
  if (settings.platform !== 'olimpbet') {
    return
  }
  const client = useApolloClient().resolveClient()
  const { ready } = initProfileLocaleSync({
    app,
    settings,
    authStore: useUserAuthStore(),
    mutate: (options) => client.mutate(options),
  })
  // Game initialization reads the profile language from the database after app mount.
  if (settings.webview) {
    return ready
  }
}
